import React from 'react';
import DodTermText from '../DodTermText';
import type { DodGlossaryTermId } from '../dodGlossary';
import {
  frequencyLabel,
  monthCells,
  type ComplianceCalendarEvent,
  type ComplianceCalendarSuggestion,
} from '../complianceCalendar';

export type PortfolioCalendarEvent = {
  source: string;
  event: ComplianceCalendarEvent;
};

export type CalendarListRow = {
  id: string;
  task: string;
  type: string;
  frequency: string;
  due: string;
};

type ComplianceCalendarProps = {
  language: 'en' | 'es';
  events: ComplianceCalendarEvent[];
  suggestions: ComplianceCalendarSuggestion[];
  portfolio: PortfolioCalendarEvent[];
  onSave: (event: ComplianceCalendarEvent) => void;
  /** Column label when every suggestion on this page is the same kind of work. */
  listGroup?: string;
  /** When set, the list tab shows these rows instead of this page's suggestions. */
  listRows?: CalendarListRow[];
  heading?: string;
  /** Feature calendars let the operator save a date. The menu calendar only shows dates already saved. */
  showEditor?: boolean;
  /** Office dates with a public source. They are not a receipt that anyone filed. */
  published?: Array<{ id: string; title: string; date: string; source: string; kind: 'file' | 'prepare' }>;
};

const weekdayLabels = (language: 'en' | 'es'): string[] => (
  language === 'es'
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
);

type CalendarSpan = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'years3' | 'years5' | 'list';

const monthGap = (from: string, to: string): number => {
  const [fromYear, fromMonth] = from.split('-').map(Number);
  const [toYear, toMonth] = to.split('-').map(Number);
  return ((toYear || 0) - (fromYear || 0)) * 12 + ((toMonth || 0) - (fromMonth || 0));
};

/** Empty months mean a month grid hides the dates. Step out until the dates are visible together. */
const smartSpan = (dates: string[], todayKey: string): CalendarSpan => {
  const unique = Array.from(new Set(dates.filter(Boolean))).sort();
  if (!unique.length) return 'year';
  const upcoming = unique.find((date) => date >= todayKey) || unique[unique.length - 1];
  const gap = monthGap(todayKey, upcoming);
  const span = monthGap(unique[0], unique[unique.length - 1]);
  if (span >= 48) return 'years5';
  if (span >= 24 || gap >= 18) return 'years3';
  if (gap >= 3 || span >= 4) return 'year';
  if (span >= 2) return 'quarter';
  const daySpan = Math.round((Date.parse(`${unique[unique.length - 1]}T00:00:00Z`) - Date.parse(`${unique[0]}T00:00:00Z`)) / 86400000);
  if (unique.length === 1 && unique[0] === todayKey) return 'day';
  if (daySpan <= 6 && gap === 0) return 'week';
  return 'month';
};

const eventTone = (source: string): string => {
  if (/prepar/i.test(source)) return 'is-prepare';
  if (/present|file/i.test(source)) return 'is-file';
  return 'is-other';
};

const ComplianceCalendar: React.FC<ComplianceCalendarProps> = ({
  language,
  events,
  suggestions,
  portfolio,
  onSave,
  listGroup = '',
  listRows,
  heading,
  showEditor = true,
  published = [],
}) => {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const datedEvents = React.useMemo(() => {
    const publishedRows: PortfolioCalendarEvent[] = published.map((row) => ({
      source: row.kind === 'prepare'
        ? (language === 'es' ? 'Preparar para' : 'Prepare by')
        : (language === 'es' ? 'Presentar el' : 'File by'),
      event: { id: row.id, title: row.title, date: row.date },
    }));
    return [
      ...portfolio.filter((row) => row.event.date && !row.event.skipped),
      ...publishedRows,
    ];
  }, [language, portfolio, published]);
  const upcoming = datedEvents
    .filter((row) => row.event.date >= todayKey)
    .slice()
    .sort((left, right) => left.event.date.localeCompare(right.event.date));
  const datedInOrder = datedEvents.slice().sort((left, right) => left.event.date.localeCompare(right.event.date));
  const latestDate = datedInOrder[datedInOrder.length - 1]?.event.date || '';
  const nextDate = upcoming[0]?.event.date || latestDate;
  const start = nextDate ? new Date(`${nextDate}T12:00:00`) : today;
  const [cursor, setCursor] = React.useState({ year: start.getFullYear(), month: start.getMonth(), day: start.getDate() });
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [span, setSpan] = React.useState<CalendarSpan>(() => smartSpan(datedEvents.map((row) => row.event.date), todayKey));
  const spanPicked = React.useRef(false);
  const syncedDates = React.useRef('');
  React.useEffect(() => {
    const key = `${datedEvents.length}:${nextDate}`;
    if (syncedDates.current === key) {
      return;
    }
    syncedDates.current = key;
    if (nextDate) {
      const when = new Date(`${nextDate}T12:00:00`);
      setCursor({ year: when.getFullYear(), month: when.getMonth(), day: when.getDate() });
    }
    if (!spanPicked.current) {
      setSpan(smartSpan(datedEvents.map((row) => row.event.date), todayKey));
    }
  }, [datedEvents, nextDate, todayKey]);
  const daysUntil = (date: string): number => {
    const then = new Date(`${date}T12:00:00`).getTime();
    const now = new Date(`${todayKey}T12:00:00`).getTime();
    return Math.round((then - now) / 86400000);
  };
  const deadlineRows = datedEvents
    .slice()
    .sort((left, right) => left.event.date.localeCompare(right.event.date));
  const deadlineGroups = [
    {
      id: 'late',
      title: language === 'es' ? 'Atrasadas' : 'Overdue',
      rows: deadlineRows.filter((row) => daysUntil(row.event.date) < 0),
    },
    {
      id: 'soon',
      title: language === 'es' ? 'En los próximos 30 días' : 'Next 30 days',
      rows: deadlineRows.filter((row) => {
        const days = daysUntil(row.event.date);
        return days >= 0 && days <= 30;
      }),
    },
    {
      id: 'later',
      title: language === 'es' ? 'Más adelante' : 'Later',
      rows: deadlineRows.filter((row) => daysUntil(row.event.date) > 30),
    },
  ].filter((group) => group.rows.length > 0);
  const copy = language === 'es';
  const dated = datedEvents;
  const chooseSpan = (next: CalendarSpan) => {
    spanPicked.current = true;
    setSpan(next);
  };
  const shift = (direction: -1 | 1) => {
    setCursor((current) => {
      const date = new Date(current.year, current.month, current.day || 1);
      if (span === 'day') date.setDate(date.getDate() + direction);
      else if (span === 'week') date.setDate(date.getDate() + direction * 7);
      else if (span === 'quarter') date.setMonth(date.getMonth() + direction * 3);
      else if (span === 'year') date.setFullYear(date.getFullYear() + direction);
      else if (span === 'years3') date.setFullYear(date.getFullYear() + direction * 3);
      else if (span === 'years5') date.setFullYear(date.getFullYear() + direction * 5);
      else date.setMonth(date.getMonth() + direction);
      return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
    });
  };
  const monthTitle = (year: number, month: number) => new Date(year, month, 1).toLocaleDateString(copy ? 'es' : 'en', {
    month: 'long',
    year: 'numeric',
  });
  const periodLabel = (() => {
    if (span === 'day') {
      return new Date(cursor.year, cursor.month, cursor.day).toLocaleDateString(copy ? 'es' : 'en', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (span === 'week') {
      const startDay = new Date(cursor.year, cursor.month, cursor.day);
      startDay.setDate(startDay.getDate() - startDay.getDay());
      const endDay = new Date(startDay);
      endDay.setDate(startDay.getDate() + 6);
      return `${startDay.toLocaleDateString(copy ? 'es' : 'en', { month: 'short', day: 'numeric' })} – ${endDay.toLocaleDateString(copy ? 'es' : 'en', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (span === 'quarter') {
      const quarter = Math.floor(cursor.month / 3) + 1;
      return copy ? `Trimestre ${quarter} de ${cursor.year}` : `Quarter ${quarter}, ${cursor.year}`;
    }
    if (span === 'years3') return `${cursor.year - 1} – ${cursor.year + 1}`;
    if (span === 'years5') return `${cursor.year - 2} – ${cursor.year + 2}`;
    if (span === 'year' || span === 'list') return String(cursor.year);
    return monthTitle(cursor.year, cursor.month);
  })();
  const spanChoices: Array<{ id: CalendarSpan; label: string }> = [
    { id: 'day', label: copy ? 'Día' : 'Day' },
    { id: 'week', label: copy ? 'Semana' : 'Week' },
    { id: 'month', label: copy ? 'Mes' : 'Month' },
    { id: 'quarter', label: copy ? 'Trimestre' : 'Quarter' },
    { id: 'year', label: copy ? 'Año' : 'Year' },
    { id: 'years3', label: copy ? '3 años' : '3 years' },
    { id: 'years5', label: copy ? '5 años' : '5 years' },
    { id: 'list', label: copy ? 'Lista' : 'List' },
  ];
  const eventsOn = (date: string) => dated.filter((row) => row.event.date === date);
  const renderMonth = (year: number, month: number, compact: boolean) => {
    const name = monthTitle(year, month);
    const grid = monthCells(year, month);
    return (
      <div className={`compliance-month${compact ? ' is-compact' : ''}`} key={`${year}-${month}`}>
        {compact ? <h4>{name}</h4> : null}
        <div className="compliance-calendar-grid" role="grid" aria-label={name}>
          {weekdayLabels(language).map((label) => (
            <span key={`${year}-${month}-${label}`} className="compliance-calendar-weekday" role="columnheader">{label}</span>
          ))}
          {grid.map((date, index) => {
            const dayEvents = date ? eventsOn(date) : [];
            return (
              <div
                key={date || `empty-${year}-${month}-${index}`}
                className={`compliance-calendar-day${date ? '' : ' is-empty'}${dayEvents.length ? ' has-event' : ''}${date === todayKey ? ' is-today' : ''}`}
                role="gridcell"
              >
                {date ? <span className="compliance-calendar-date">{Number(date.slice(-2))}</span> : null}
                {dayEvents.slice(0, compact ? 1 : 3).map((row) => (
                  <span key={`${row.source}-${row.event.id}`} className={`compliance-cal-chip ${eventTone(row.source)}`}>
                    <i aria-hidden="true" />
                    {row.event.title}
                  </span>
                ))}
                {dayEvents.length > (compact ? 1 : 3) ? <span className="compliance-cal-more">+{dayEvents.length - (compact ? 1 : 3)}</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  const seen = new Set<DodGlossaryTermId>();
  const builtRows: CalendarListRow[] = suggestions.map((suggestion) => {
    const saved = events.find((event) => event.id === suggestion.id);
    let due = '';
    if (saved?.skipped) {
      due = copy ? 'No este año' : 'Not this year';
    } else if (saved?.date) {
      due = saved.date;
    }
    return {
      id: suggestion.id,
      task: suggestion.title[language],
      type: listGroup,
      frequency: frequencyLabel(suggestion.frequency, language),
      due,
    };
  });
  const rows = listRows || builtRows;

  return (
    <section className="compliance-calendar" aria-label={copy ? 'Calendario de cumplimiento' : 'Compliance Calendar'}>
      <div className="compliance-calendar-head">
        <h3>{heading || (copy ? 'Calendario de todos los proyectos' : 'Calendar for all compliance projects')}</h3>
        <div className="compliance-calendar-tabs" role="tablist" aria-label={copy ? 'Vista del calendario' : 'Calendar view'}>
          {spanChoices.map((choice) => (
            <button
              key={choice.id}
              type="button"
              role="tab"
              aria-selected={span === choice.id}
              aria-label={choice.label}
              className={span === choice.id ? 'is-selected' : ''}
              onClick={() => chooseSpan(choice.id)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {choice.id === 'day' ? <rect x="7" y="5" width="10" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" /> : null}
                {choice.id === 'week' ? <path d="M4 7h2.2v10H4zm3.4 0h2.2v10H7.4zm3.4 0h2.2v10h-2.2zm3.4 0h2.2v10h-2.2zm3.4 0H20v10h-2.4z" fill="currentColor" /> : null}
                {choice.id === 'month' ? (
                  <>
                    <rect x="3.5" y="5" width="17" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M3.5 9.5h17M8 5v2.2M16 5v2.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
                  </>
                ) : null}
                {choice.id === 'quarter' ? <path d="M3 6h5v12H3zm6.5 0h5v12h-5zM16 6h5v12h-5z" fill="none" stroke="currentColor" strokeWidth="1.6" /> : null}
                {choice.id === 'year' ? <path d="M4 5h7v6H4zm9 0h7v6h-7zM4 13h7v6H4zm9 0h7v6h-7z" fill="none" stroke="currentColor" strokeWidth="1.6" /> : null}
                {choice.id === 'years3' ? <path d="M3 6h5v12H3zm6.5 6h5v6h-5zM16 6h5v12h-5z" fill="none" stroke="currentColor" strokeWidth="1.6" /> : null}
                {choice.id === 'years5' ? <path d="M2 8h3.2v8H2zm4.2 8h3.2v-5H6.2zm4.2 5h3.2V8h-3.2zm4.2 8h3.2v-6h-3.2zM18.8 8H22v8h-3.2z" fill="currentColor" /> : null}
                {choice.id === 'list' ? <path d="M5 7h14M5 12h14M5 17h10" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /> : null}
              </svg>
              <span className="compliance-calendar-tip">{choice.label}</span>
            </button>
          ))}
        </div>
      </div>
      {published.length && span !== 'list' ? (
        <p className="compliance-calendar-note">
          {copy
            ? 'Presentar el es la fecha de la oficina. Preparar para es 30 días antes. Ninguna de las dos es un recibo de que ya se presentó.'
            : 'File by is the office date. Prepare by is 30 days earlier. Neither one is a receipt that the paper was filed.'}
        </p>
      ) : null}

      {span === 'list' ? (
        deadlineGroups.length ? (
          <div className="compliance-deadline-board">
            {deadlineGroups.map((group) => (
              <section key={group.id} aria-label={group.title}>
                <h4>{group.title}</h4>
                <ul>
                  {group.rows.map((row) => {
                    const days = daysUntil(row.event.date);
                    const when = days < 0
                      ? (copy ? `Hace ${-days} días` : `${-days} days past`)
                      : days === 0
                        ? (copy ? 'Hoy' : 'Due today')
                        : (copy ? `En ${days} días` : `Due in ${days} days`);
                    return (
                      <li key={`${row.source}-${row.event.id}-${row.event.date}`}>
                        <time dateTime={row.event.date}>{row.event.date}</time>
                        <strong>{row.event.title}</strong>
                        <span className={days < 0 ? 'is-late' : days <= 30 ? 'is-soon' : undefined}>{when} · {row.source}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="compliance-deadline-board">
            {rows.filter((row) => row.due).map((row) => (
              <li key={row.id}>
                <time>{row.due}</time>
                <strong><DodTermText text={row.task} language={language} seen={seen} /></strong>
                <span>{row.type}{row.frequency ? ` · ${row.frequency}` : ''}</span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          <p className="compliance-cal-legend">
            <span className="compliance-cal-chip is-prepare"><i aria-hidden="true" />{copy ? 'Preparar para' : 'Prepare by'}</span>
            <span className="compliance-cal-chip is-file"><i aria-hidden="true" />{copy ? 'Presentar el' : 'File by'}</span>
          </p>
          <div className="compliance-calendar-nav">
            <button type="button" onClick={() => shift(-1)} aria-label={copy ? 'Anterior' : 'Previous'}>
              {copy ? 'Anterior' : 'Previous'}
            </button>
            <strong>{periodLabel}</strong>
            <button type="button" onClick={() => shift(1)} aria-label={copy ? 'Siguiente' : 'Next'}>
              {copy ? 'Siguiente' : 'Next'}
            </button>
          </div>
          {span === 'month' ? renderMonth(cursor.year, cursor.month, false) : null}
          {span === 'quarter' ? (
            <div className="compliance-quarter">
              {[0, 1, 2].map((offset) => {
                const date = new Date(cursor.year, Math.floor(cursor.month / 3) * 3 + offset, 1);
                return renderMonth(date.getFullYear(), date.getMonth(), true);
              })}
            </div>
          ) : null}
          {span === 'day' || span === 'week' ? (
            <div className={`compliance-span-days is-${span}`}>
              {(() => {
                const startDay = new Date(cursor.year, cursor.month, cursor.day);
                if (span === 'week') startDay.setDate(startDay.getDate() - startDay.getDay());
                const count = span === 'week' ? 7 : 1;
                return Array.from({ length: count }, (_, index) => {
                  const date = new Date(startDay);
                  date.setDate(startDay.getDate() + index);
                  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const dayEvents = eventsOn(key);
                  return (
                    <section key={key} className={key === todayKey ? 'is-today' : undefined}>
                      <h4>{date.toLocaleDateString(copy ? 'es' : 'en', { weekday: 'short', month: 'short', day: 'numeric' })}</h4>
                      {dayEvents.length ? dayEvents.map((row) => (
                        <p key={`${row.source}-${row.event.id}`} className={`compliance-cal-chip ${eventTone(row.source)}`}>
                          <i aria-hidden="true" />
                          {row.event.title}
                          <span>{row.source}</span>
                        </p>
                      )) : <p className="compliance-calendar-note">{copy ? 'Sin fechas' : 'No dates'}</p>}
                    </section>
                  );
                });
              })()}
            </div>
          ) : null}
          {span === 'year' || span === 'years3' || span === 'years5' ? (
            <div className={`compliance-year-board is-${span}`}>
              {Array.from({ length: span === 'year' ? 1 : span === 'years3' ? 3 : 5 }, (_, index) => {
                const year = cursor.year + index - (span === 'years3' ? 1 : span === 'years5' ? 2 : 0);
                return (
                  <section key={year}>
                    {span === 'year' ? null : <h4>{year}</h4>}
                    <div className="compliance-year-grid">
                      {Array.from({ length: 12 }, (_, month) => {
                        const monthEvents = dated.filter((row) => row.event.date.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`));
                        return (
                          <button
                            key={month}
                            type="button"
                            className={monthEvents.length ? 'has-event' : ''}
                            onClick={() => {
                              spanPicked.current = true;
                              setCursor({ year, month, day: 1 });
                              setSpan('month');
                            }}
                          >
                            <strong>{new Date(year, month, 1).toLocaleDateString(copy ? 'es' : 'en', { month: 'short' })}</strong>
                            {monthEvents.slice(0, 2).map((row) => (
                              <span key={`${row.event.id}-${row.event.date}`} className={`compliance-cal-chip ${eventTone(row.source)}`}>
                                <i aria-hidden="true" />
                                {row.event.title}
                              </span>
                            ))}
                            {monthEvents.length > 2 ? <em>+{monthEvents.length - 2}</em> : null}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}
          {!dated.length ? (
            <p className="compliance-calendar-note">
              {copy ? 'Todavía no hay fechas de presentación en este trabajo.' : 'No filing dates are on this work yet.'}
            </p>
          ) : null}
        </>
      )}

      {showEditor ? <h4>{copy ? 'Fechas de este trabajo' : 'Dates for this work'}</h4> : null}
      {showEditor ? <p className="compliance-calendar-note">
        {copy
          ? 'La frecuencia dice cada cuánto se repite el trabajo. La fecha la elige usted. No hay una fecha oficial salvo que el paso lo diga.'
          : 'Frequency says how often the work repeats. You choose the date. There is no official date unless the step says so.'}
      </p> : null}
      {showEditor ? <ul className="compliance-calendar-suggestions">
        {suggestions.map((suggestion) => {
          const saved = events.find((event) => event.id === suggestion.id);
          const title = suggestion.title[language];
          return (
            <li key={suggestion.id}>
              <div>
                <strong><DodTermText text={title} language={language} seen={seen} /></strong>
                <p>{frequencyLabel(suggestion.frequency, language)}. {suggestion.hint[language]}</p>
                {saved ? (
                  <p className="compliance-calendar-saved">
                    {saved.skipped
                      ? (copy ? 'No aplica este año.' : 'Not this year.')
                      : (copy ? `En el calendario: ${saved.date}` : `On the calendar: ${saved.date}`)}
                  </p>
                ) : null}
              </div>
              <label>
                <span className="visually-hidden">{title}</span>
                <input
                  type="date"
                  value={drafts[suggestion.id] ?? (saved?.date || '')}
                  onChange={(event) => setDrafts((current) => ({ ...current, [suggestion.id]: event.target.value }))}
                />
              </label>
              <button
                type="button"
                className="dashboard-btn dashboard-btn-primary"
                onClick={() => {
                  const date = drafts[suggestion.id] ?? saved?.date ?? '';
                  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
                    return;
                  }
                  onSave({ id: suggestion.id, title, date, skipped: false });
                }}
              >
                {copy ? 'Poner en el calendario' : 'Put on the calendar'}
              </button>
              <button
                type="button"
                className="dashboard-btn"
                onClick={() => onSave({ id: suggestion.id, title, date: '', skipped: true })}
              >
                {copy ? 'No este año' : 'Not this year'}
              </button>
            </li>
          );
        })}
      </ul> : null}
    </section>
  );
};

export default ComplianceCalendar;
